import { bannerSceneUrl } from "../shops/shopsArt";
import type { ArmourySlotView, TreasureView, TrophyHallView } from "@highmarch/app";
import { RARITY_IDS } from "@highmarch/domain";
import { bindSeg, bump, fmtCompact, segHtml, tickNumber } from "../kit";
import { toast } from "../toast";
import { chestIconUrl, uiUrl } from "../portraits";
import { rewardChest } from "../chestReveal";
import { CurrencyKey, PriceCurrency, currencyPillsHtml, emptyHtml, heroHtml, lockChipHtml, priceButtonHtml, secHtml, slotTileHtml, tagHtml, timerChipHtml, wellHtml } from "../components";
import { econCue, purchasePop } from "../hub/purchaseFx";
import { openItemPopup } from "../shops/itemPopup";
import { fmtCountdown } from "../deals/dealsModel";
import { oddsHtml } from "../deals/pickSheet";
import { paintedIconUrl } from "../featureIcons";
import { essenceIconUrl, sigilIconUrl } from "../forgeArt";
import { supplyIconHtml } from "../supplies/supplyArt";
import { trophiesIcon } from "../hub/econArt";
import { HERO_FOCUS } from "../shops/shopsArt";
import { TrophyLink } from "./trophyLink";
import { TROPHY_TABS, TrophyTab, entryLook, treasureArtPath, treasureBlurb, treasureName, treasureState, trophyRefusal } from "./trophyHallModel";
import "../styles/trophyHall.css";

/**
 * The Trophy Hall page of the Shops sheet (MP-23, docs/SHOP.md §9.7): the shop skin and layout of the Merchant and
 * Event pages — Captain Bram's hero (the Armoury's restock countdown), then Treasures (the exclusive cosmetics, each
 * once) or the Armoury (the week's shelf, "Left: n" a week), each in a 3-column grid of wells with the gold Trophy
 * price. A tap confirms what it is and costs (a chest's odds one tap away, a supplies pack's three supplies named) and
 * buys it as shown. The Shops sheet draws the header and the tabs; this draws the page and handles its taps.
 */

/** Under this many seconds the restock countdown turns red (as the shops'). */
const URGENT_S = 3600;
/** How long a bought tile's glow plays before the page redraws it. */
const REVEAL_MS = 700;
/** The item popup's stamps where BUY would be (owner 2026-10-24: a tile opens even when it can't be bought). */
const OWNED_STAMP = "Yours", SOLD_STAMP = "Sold out";
/** The Trophies currency as the kit's price and pill builders take it (components.ts CurrencyKey is gems | Marks;
 * shopsArt.currencyIcon, which they call, draws the Trophies token: foundation request to widen the key). */
const TROPHIES = "trophies" as unknown as CurrencyKey & PriceCurrency;

/** The Trophy Hall's header pill: Trophies held. */
export function trophyPillsHtml(n: number): string { return currencyPillsHtml([{ currency: TROPHIES, amount: fmtCompact(n) }]); }

export class TrophyHallPage {
  tab: TrophyTab = "treasures";
  private busy = false;

  constructor(private readonly l: TrophyLink, private readonly parent: () => HTMLElement, private readonly redraw: () => void) {}

  view(): TrophyHallView { return this.l.hall.view(this.l.now()); }

  /** The page: the hero, the Treasures · Armoury switch, and the tab's grid (or the closed Hall's teaser). */
  bodyHtml(): string {
    const v = this.view(), now = this.l.now();
    if (!v.open) {
      const hero = heroHtml({ art: uiUrl(TROPHY_HERO), title: "Trophy Hall", timer: lockChipHtml(`Level ${this.l.catalog.balance.trophies.unlockAfterLevel}`), className: "shero trophy", bright: { theme: "camp", cast: uiUrl("captain/bram_thumbsup@3x.png"), prop: uiUrl(TROPHY_TAB_ICON), scene: bannerSceneUrl("trophy") } });
      return `<section class="spage trophy closed">${hero}<p class="steaser">Clear level ${this.l.catalog.balance.trophies.unlockAfterLevel}, or win your first Trophies, to open the Trophy Hall.</p></section>`;
    }
    const left = v.endsAt - now;
    const timer = timerChipHtml(fmtCountdown(left), { endsAt: v.endsAt, urgent: left < URGENT_S });
    const hero = heroHtml({ art: uiUrl(TROPHY_HERO), title: "Trophy Hall", timer, className: "shero trophy", bright: { theme: "camp", cast: uiUrl("captain/bram_thumbsup@3x.png"), prop: uiUrl(TROPHY_TAB_ICON), scene: bannerSceneUrl("trophy") } });
    const seg = `<div class="trseg">${segHtml(TROPHY_TABS, this.tab, "Trophy Hall")}</div>`;
    return `<section class="spage trophy t-${this.tab}">${hero}${seg}${this.tab === "treasures" ? this.treasuresHtml(v) : this.armouryHtml(v)}</section>`;
  }

  private treasuresHtml(v: TrophyHallView): string {
    if (v.treasures.length === 0) return `${emptyHtml(paintedIconUrl("trophies", 3), "New treasures are on their way")}<p class="sfoot">${FOOT_EARN}</p>`;
    const owned = v.treasures.filter((t) => t.owned).length;
    return `${secHtml("Treasures", `${owned} of ${v.treasures.length} yours`)}<div class="k-grid3 sslots trgrid">${v.treasures.map(treasureTileHtml).join("")}</div>
      <p class="sfoot">Yours for good: looks only, pure glory. ${FOOT_EARN}</p>`;
  }

  private armouryHtml(v: TrophyHallView): string {
    const B = this.l.catalog.balance;
    const tiles = v.armoury.map((s) => armouryTileHtml(B, s)).join("");
    return `${secHtml("This week's Armoury", `Week ${v.isoWeek}`)}<div class="k-grid3 sslots trgrid">${tiles}</div>
      <p class="sfoot">New stock every Monday, 00:00 UTC. Each item has its own weekly limit. ${FOOT_EARN}</p>`;
  }

  /** Wires the page: the Treasures · Armoury switch and the tiles (the Shops sheet has wired its own tabs first). */
  bind(root: HTMLElement): void {
    const page = root.querySelector<HTMLElement>(".spage.trophy");
    if (!page) return;
    const seg = page.querySelector<HTMLElement>(".trseg");
    if (seg) bindSeg(seg, (k) => { if (k !== this.tab) { this.tab = k as TrophyTab; econCue("tap"); this.redraw(); } });
    if (page.classList.contains("closed")) return;
    for (const t of page.querySelectorAll<HTMLElement>(".trtile[data-skin]")) t.onclick = () => void this.buyTreasure(t.dataset.skin ?? "", t);
    for (const t of page.querySelectorAll<HTMLElement>(".trtile[data-entry]")) t.onclick = () => void this.buyEntry(t.dataset.entry ?? "", t);
  }

  // ------------------------------------------------------------------ buying

  private async buyTreasure(id: string, tile: HTMLElement): Promise<void> {
    if (this.busy) return;
    const t = this.view().treasures.find((x) => x.skin.id === id);
    if (!t) { this.redraw(); return; }
    // owner 2026-10-24: every tile opens its popup; short of Trophies, BUY says so
    if (t.blocked && t.blocked !== "not enough" && t.blocked !== "owned") { this.refuse(t.blocked); return; }
    this.busy = true;
    let ok = false;
    try {
      const body = `<div class="scitem">${wellHtml({ art: treasureArt(t), size: "lg" })}<div class="sct"><b class="disp">${treasureName(t.skin)}</b><small>${treasureBlurb(t.skin)}. Yours for good.</small></div></div>`;
      ok = await openItemPopup(this.parent(), { title: "TROPHY HALL", price: `${trophiesIcon(20)} ${fmtCompact(t.price)}`, className: "c-trophies", body, blocked: t.blocked === "owned" ? OWNED_STAMP : "" });
    } finally { this.busy = false; }
    if (!ok) return;
    if (t.blocked) { this.refuse(t.blocked); return; }
    const before = this.l.hall.trophies, r = this.l.hall.buyTreasure(id, this.l.now());
    if (r !== true) { this.refuse(r); this.redraw(); return; }
    this.done(tile, before, `${treasureName(t.skin)} is yours: wear it from Skins`);
  }

  private async buyEntry(id: string, tile: HTMLElement): Promise<void> {
    if (this.busy) return;
    const s = this.view().armoury.find((x) => x.entry.id === id);
    if (!s) { this.redraw(); return; }
    const stamp = s.blocked === "sold out" ? SOLD_STAMP : s.blocked === "locked" && s.entry.minLevel > 0 ? `Level ${s.entry.minLevel}` : "";
    if (s.blocked && s.blocked !== "not enough" && !stamp) { this.refuse(s.blocked, s.entry.minLevel); return; }
    this.busy = true;
    let ok = false;
    const B = this.l.catalog.balance, look = entryLook(B, s);
    try {
      const odds = look.odds ? `<div class="sodds2"><b class="ifhead">What's inside</b>${oddsHtml(B, look.odds)}</div>` : "";   // no ODDS button (2026-10-21)
      const sup = s.supplies.length > 0 ? `<div class="trsups">${s.supplies.map((x) => supplyIconHtml(x, 36)).join("")}</div>` : "";
      const ink = look.rarity === null ? "" : ` ri-${RARITY_IDS[look.rarity]}`;
      const body = `<div class="scitem">${wellHtml({ art: entryArt(s), rarity: look.rarity, size: "lg", count: look.count })}<div class="sct"><b class="disp${ink}">${look.name}</b><small>${look.detail}</small></div></div>${sup}${odds}`;
      ok = await openItemPopup(this.parent(), { title: "TROPHY HALL", price: `${trophiesIcon(20)} ${fmtCompact(s.entry.price)}`, className: "c-trophies", body, blocked: stamp });
    } finally { this.busy = false; }
    if (!ok) return;
    if (s.blocked) { this.refuse(s.blocked, s.entry.minLevel); return; }
    const before = this.l.hall.trophies, r = this.l.hall.buyArmoury(id, s.bought, this.l.now());
    if (typeof r === "string") { this.refuse(r, s.entry.minLevel); this.redraw(); return; }
    if (r.opened) {
      this.l.changed();
      econCue("trade");
      this.redraw();
      this.tickPill(before, this.l.hall.trophies);
      const o = r.opened;
      rewardChest(this.parent(), B, o.chest.rarity, { gear: [...o.gear], speedUpMinutes: o.chest.speedUpMinutes, gold: o.chest.gold }, look.name, () => undefined);
      return;
    }
    this.done(tile, before, `${look.name} ${r.supplies.length > 0 ? "are" : "is"} yours`);
  }

  /** A purchase landed: save, the tile glows, the pill rolls down, a toast says it, then the page redraws. */
  private done(tile: HTMLElement, before: number, line: string): void {
    this.l.changed();
    econCue("trade");
    purchasePop(tile);
    this.tickPill(before, this.l.hall.trophies);
    toast(this.parent(), line);
    this.busy = true;
    window.setTimeout(() => { this.busy = false; this.redraw(); }, REVEAL_MS);
  }

  private refuse(code: string, minLevel = 0): void {
    toast(this.parent(), trophyRefusal(code, minLevel));
    if (code === "not enough" || code === "not enough Trophies") bump(this.pill(), 1.15, 220);
  }

  private pill(): HTMLElement | null { return this.parent().querySelector<HTMLElement>(".sheet.shops .k-pills .pill[data-cur=trophies]"); }

  private tickPill(from: number, to: number): void {
    const n = this.pill()?.querySelector<HTMLElement>(":scope > span");
    if (!n || from === to) return;
    tickNumber(n, from, to, 450, fmtCompact);
    bump(n.parentElement, 1.12, 200);
  }
}

/** The hero (art/interface/shops/trophy_hero.webp: Captain Bram in the hall of honours, tools/art/kit/heroes_bpy.py). */
export const TROPHY_HERO = "shops/trophy_hero.webp";
/** The keeper's header portrait and the Shops tab icon. */
export const TROPHY_PORTRAIT = "icons/shop_champion.png";
export const TROPHY_TAB_ICON = "icons/shop_tab_trophy.png";
const KICKER = "Champion Bram";
const FOOT_EARN = "Trophies are won, never sold: Boss Week, Race Week, the Odyssey, weekly tracks, the Trial, Endless and Iron.";

/** A Treasure's picture with the Trophies token as its fallback (a cosmetic whose swatch has not landed). */
function treasureArt(t: TreasureView): string {
  const fb = paintedIconUrl("trophies", 3);
  return `<img class="k-art" decoding="async" src="${uiUrl(treasureArtPath(t.skin))}" alt="" onerror="this.onerror=null;this.src='${fb}'">`;
}

function treasureTileHtml(t: TreasureView): string {
  const st = treasureState(t), name = treasureName(t.skin);
  const price = st === "owned" ? `<span class="trowned">${tagHtml("best", "OWNED")}</span>`
    : priceButtonHtml({ currency: TROPHIES, amount: fmtCompact(t.price), short: st === "short", className: "sbuy", data: `aria-label="${name}, ${t.price} Trophies"` });
  return slotTileHtml({ well: { art: treasureArt(t), dim: t.owned }, name, price, className: `stile trtile s-${st} tap`, data: `data-skin="${t.skin.id}"` });
}

/** An Armoury entry's picture: its painted icon; a supplies pack shows the week's three supplies themselves. */
function entryArt(s: Pick<ArmourySlotView, "entry" | "supplies">): string {
  const r = s.entry.reward;
  if (r === null) return `<span class="trsupart">${s.supplies.map((x) => supplyIconHtml(x, 24)).join("")}</span>`;
  if (r.chest !== null) return chestIconUrl(RARITY_IDS[r.chest]);
  if (r.sigil !== null) return sigilIconUrl(r.sigil);
  return essenceIconUrl();
}

function armouryTileHtml(B: Parameters<typeof entryLook>[0], s: ArmourySlotView): string {
  const look = entryLook(B, s);
  const state = s.locked ? "locked" : s.left <= 0 ? "sold" : "ok";
  const price = priceButtonHtml({ currency: TROPHIES, amount: fmtCompact(s.entry.price), short: s.blocked === "not enough", className: "sbuy", data: `aria-label="${look.name}, ${s.entry.price} Trophies"` });
  return slotTileHtml({
    well: { art: entryArt(s), rarity: look.rarity, count: look.count, info: look.odds !== null }, name: look.name, left: `Left: ${s.left}`, price, state, lock: `Level ${s.entry.minLevel}`,
    className: `stile trtile s-${state === "ok" ? (s.blocked === "not enough" ? "short" : "ok") : state} tap`, data: `data-entry="${s.entry.id}"`,
  });
}
